<div class="creators-table-container">
  <app-datatable
    [rowData]="creators()"
    [columns]="datatableColumns()"
    [configurationInput]="datatableConfig"
    [customColumnDef]="[
      { name: 'creator', isSortable: true, template: creatorColumn },
      { name: 'invited', isSortable: true, template: invitedColumn },
      { name: 'briefed', isSortable: true, template: briefedColumn },
      { name: 'payout', isSortable: true, template: payoutColumn },
      { name: 'platforms', isSortable: false, template: platformsColumn },
      { name: 'status', isSortable: true, template: statusColumn },
      { name: 'actions', isSortable: false, template: actionsColumn },
    ]"
    [total]="creators().length"
    [loaderConfig]="skeletonLoaderConfig()"
    [isInitialDataLoaded]="!loading()"
    [loadingData]="loading()"
    (rowClick)="onRowClick($event)"
  />
</div>

<!-- Creator column -->
<ng-template #creatorColumn let-row="row">
  @let canViewCreator = !creatorBriefingMode() && !exportMode();
  <div class="flex items-center gap-3 py-2">
    <div
      class="flex items-center gap-3 rounded-lg p-2 -m-2 transition-colors"
      matTooltipPosition="above"
      [class.cursor-pointer]="canViewCreator"
      [class.hover:bg-shikenso-pink-base-12]="canViewCreator"
      [matTooltip]="canViewCreator ? ('pages.campaignDetails.table.viewCreator' | translate) : ''"
      [matTooltipShowDelay]="500"
      (click)="canViewCreator && onViewCreator(row, $event)"
    >
      <app-icon-badge
        [content]="row.icon"
        [fallbackText]="row.name"
        [size]="40"
        [iconSize]="40"
        [fontSize]="16"
        [borderRadius]="100"
      />
      <span
        class="text-sm text-ellipsis line-clamp-1"
        matTooltip
        matTooltipPosition="above"
        [appTextClampTooltip]="row.name"
      >
        {{ row.name }}
      </span>
    </div>
  </div>
</ng-template>

<!-- Invited column -->
<ng-template #invitedColumn let-row="row">
  @if (invitedCreatorIds().has(row.id)) {
    <div class="w-4 h-4 rounded-full bg-shikenso-success-base flex items-center justify-center">
      <app-icon src="/icons/check.svg" alt="invited" color="white" [size]="12" />
    </div>
  } @else {
    <div class="w-4 h-4 rounded-full bg-shikenso-error-bright flex items-center justify-center">
      <app-icon src="/icons/x-mark.svg" alt="not invited" color="white" [size]="12" />
    </div>
  }
</ng-template>

<!-- Briefed column -->
<ng-template #briefedColumn let-row="row">
  @if (creatorCampaignExports().get(row.id)?.isActive) {
    <div class="w-4 h-4 rounded-full bg-shikenso-success-base flex items-center justify-center">
      <app-icon src="/icons/check.svg" alt="briefed" color="white" [size]="12" />
    </div>
  } @else {
    <div class="w-4 h-4 rounded-full bg-shikenso-error-bright flex items-center justify-center">
      <app-icon src="/icons/x-mark.svg" alt="not briefed" color="white" [size]="12" />
    </div>
  }
</ng-template>

<!-- Payout column -->
<ng-template #payoutColumn let-row="row">
  <span class="text-sm">
    {{ campaignCreatorsMap().get(row.id)?.cost ?? 0 | numberSuffix: true }}
    {{ campaign()?.currency }}
  </span>
</ng-template>

<!-- Status column — float-ui anchor; row click triggers this cell programmatically -->
<ng-template #statusColumn let-row="row">
  <div
    class="flex items-center gap-1 cursor-pointer w-fit"
    [attr.data-creator-status-cell]="row.id"
    #statusRef="floatUi"
    [floatUi]="rowPopover"
    [placement]="floatUiPlacementBottom"
    [showTrigger]="floatUiTriggerNone"
    [hideOnClickOutside]="false"
    (click)="onStatusCellClick(row, statusRef, $event)"
  >
    <span class="w-3 h-3 rounded-full shrink-0" [class]="getStatusDotClass(row.mergedStatus)"></span>
    <span class="text-sm font-medium">{{ getStatusLabel(row.mergedStatus) | translate }}</span>
    @if (hasIssues(row)) {
      <span class="text-xs text-shikenso-purple-base-60">
        {{ 'pages.campaignDetails.creators.table.issueCount' | translate: { count: getIssueCount(row) } }}
      </span>
    }
  </div>
</ng-template>

<!-- Platforms column -->
<ng-template #platformsColumn let-row="row">
  <div class="flex items-center gap-1">
    @for (usedPlatform of row.usedPlatforms; track getUsedPlatformTracking(usedPlatform)) {
      <a
        class="hover:scale-110 transition-all duration-75"
        target="_blank"
        [href]="generateSocialUrl(usedPlatform)"
        (click)="$event.stopPropagation()"
      >
        <app-social-badge backgroundColor="transparent" [type]="usedPlatform.platform" [size]="16" [iconSize]="16" />
      </a>
    }
  </div>
</ng-template>

<!-- Actions column -->
<ng-template #actionsColumn let-row="row">
  @if (!creatorBriefingMode() && !exportMode()) {
    <div (click)="$event.stopPropagation()">
      <app-creator-actions-menu
        [creator]="row"
        [campaign]="campaign()"
        [creatorCampaignExports]="creatorCampaignExports()"
        [viewMode]="viewMode"
        [currentTab]="currentTab"
      />
    </div>
  }
</ng-template>

<!-- Shared popover for all rows -->
<float-ui-content #rowPopover class="z-50">
  @if (selectedCreator(); as creator) {
    <div class="min-w-52 max-w-72" (click)="$event.stopPropagation()">
      @switch (creator.mergedStatus) {
        @case (EAnalyticsStatus.ok) {
          <div class="flex flex-col gap-1">
            <button
              type="button"
              class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 py-1 hover:bg-shikenso-pink-base-12 transition-all text-left"
              (click)="onAllGoodClick(creator, $event)"
            >
              <span class="w-2.5 h-2.5 rounded-full shrink-0 bg-shikenso-success-base"></span>
              <span class="text-xs">{{ creator.ctaKpiStatus.currentOkContents }}</span>
              <span class="text-xs">{{ 'pages.campaignDetails.creators.popup.matchedContent' | translate }}</span>
            </button>
          </div>
        }

        @case (EAnalyticsStatus.processing) {
          <div class="flex flex-col gap-1">
            @if (creator.ctaKpiStatus.currentMissingContents > 0) {
              <button
                type="button"
                class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 py-1 hover:bg-shikenso-pink-base-12 transition-all text-left"
                (click)="onMissingContentsClick(creator, $event)"
              >
                <span class="w-2.5 h-2.5 rounded-full shrink-0 bg-shikenso-error-bright"></span>
                <span class="text-xs">
                  {{
                    'pages.campaignDetails.overview.missingContents'
                      | translate: { count: creator.ctaKpiStatus.currentMissingContents }
                  }}
                </span>
              </button>
            }

            @if (creator.ctaKpiStatus.expectedContents - creator.ctaKpiStatus.currentMissingContents > 0) {
              <button
                type="button"
                class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 py-1 hover:bg-shikenso-pink-base-12 transition-all text-left"
                (click)="onNotStartedClick(creator, $event)"
              >
                <span class="w-2.5 h-2.5 rounded-full shrink-0 bg-shikenso-purple-base-60"></span>
                <span class="text-xs">
                  {{ creator.ctaKpiStatus.expectedContents - creator.ctaKpiStatus.currentMissingContents }}
                </span>
                <span class="text-xs">{{ 'pages.campaignDetails.creators.popup.plannedContent' | translate }}</span>
              </button>
            }
          </div>
        }

        @default {
          <div class="flex flex-col gap-1">
            @for (row of getPopoverRows(creator); track row.label) {
              <button
                type="button"
                class="flex w-fit items-center gap-1.5 cursor-pointer rounded px-1 py-1 hover:bg-shikenso-pink-base-12 transition-all text-left"
                (click)="onPopoverRowClick(row, $event)"
              >
                <span class="w-2.5 h-2.5 rounded-full shrink-0" [class]="row.colorClass"></span>
                <span class="text-xs">{{ row.label | translate: { count: row.count } }}</span>
              </button>
            }
          </div>
        }
      }
    </div>
  }
</float-ui-content>
